import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Package, ShoppingBag, ArrowRight } from 'lucide-react';
import client from '../../api/client';
import { ORDER_STATUS_LABELS } from '../../constants/statuses';
import { useApp } from '../../context/AppContext';
import { formatPrice } from '../../utils/price';

const STATUS_LABELS = {
  pending: { text: ORDER_STATUS_LABELS.pending, color: 'bg-[#E8A838]/20 text-[#E8A838]' },
  paid: { text: ORDER_STATUS_LABELS.paid, color: 'bg-[#6B9B74]/20 text-[#6B9B74]' },
  preparing: { text: ORDER_STATUS_LABELS.preparing, color: 'bg-[#4A6FA5]/20 text-[#4A6FA5]' },
  ready: { text: ORDER_STATUS_LABELS.ready, color: 'bg-[#4A6FA5]/20 text-[#4A6FA5]' },
  delivered: { text: ORDER_STATUS_LABELS.delivered, color: 'bg-[#64748b]/20 text-[#64748b]' },
  cancelled: { text: ORDER_STATUS_LABELS.cancelled, color: 'bg-[#D64545]/20 text-[#D64545]' },
  reserved: { text: ORDER_STATUS_LABELS.reserved, color: 'bg-[#E8A838]/20 text-[#E8A838]' },
};

function formatAmount(val) {
  return formatPrice(val);
}

export default function Dashboard() {
  const { user } = useApp();
  const isManager = user?.is_admin && user?.role === 'manager';
  const [stats, setStats] = useState(null);
  const [attentionOrders, setAttentionOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, attentionRes] = await Promise.all([
          client.get('/admin/dashboard/stats'),
          client.get('/admin/dashboard/attention'),
        ]);
        setStats(statsRes.data);
        setAttentionOrders(attentionRes.data || []);
      } catch (err) {
        console.error('Ошибка загрузки дашборда:', err);
        setLoadError('Не удалось загрузить дашборд');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6B9B74]"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 space-y-4 animate-fade-in">
      {loadError && (
        <div className="rounded-xl border border-[#D64545]/30 bg-[#D64545]/10 p-4 text-sm text-[#D64545]">
          {loadError}
        </div>
      )}
      {/* Первая строка: Клиенты, Товары, Заказы */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white p-3 ring-1 ring-[#e2e8f0] text-center">
          <div className="mx-auto mb-2 grid h-9 w-9 place-items-center rounded-xl bg-[#4A6FA5]/15 text-[#4A6FA5]">
            <Users className="h-5 w-5" />
          </div>
          <div className="text-lg font-bold text-[#0f172a]">{stats?.users_count || 0}</div>
          <div className="text-[10px] text-[#64748b]">Клиентов</div>
        </div>
        <div className="rounded-2xl bg-white p-3 ring-1 ring-[#e2e8f0] text-center">
          <div className="mx-auto mb-2 grid h-9 w-9 place-items-center rounded-xl bg-[#E8A838]/15 text-[#E8A838]">
            <Package className="h-5 w-5" />
          </div>
          <div className="text-lg font-bold text-[#0f172a]">{stats?.products_count || 0}</div>
          <div className="text-[10px] text-[#64748b]">Товаров</div>
        </div>
        <div className="rounded-2xl bg-white p-3 ring-1 ring-[#e2e8f0] text-center">
          <div className="mx-auto mb-2 grid h-9 w-9 place-items-center rounded-xl bg-[#4A6FA5]/15 text-[#4A6FA5]">
            <ShoppingBag className="h-5 w-5" />
          </div>
          <div className="text-lg font-bold text-[#0f172a]">{stats?.active_orders_count || 0}</div>
          <div className="text-[10px] text-[#64748b]">Активных</div>
        </div>
      </div>

      {/* Выручка — только для owner/developer */}
      {!isManager && (
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-2xl bg-white p-3 ring-1 ring-[#e2e8f0] text-center">
            <div className="text-[10px] text-[#64748b] mb-1">Сегодня</div>
            <div className="text-sm font-bold text-[#6B9B74]">{formatAmount(stats?.revenue_today)} ₽</div>
          </div>
          <div className="rounded-2xl bg-white p-3 ring-1 ring-[#e2e8f0] text-center">
            <div className="text-[10px] text-[#64748b] mb-1">Месяц</div>
            <div className="text-sm font-bold text-[#6B9B74]">{formatAmount(stats?.revenue_month)} ₽</div>
          </div>
          <div className="rounded-2xl bg-white p-3 ring-1 ring-[#e2e8f0] text-center">
            <div className="text-[10px] text-[#64748b] mb-1">Год</div>
            <div className="text-sm font-bold text-[#6B9B74]">{formatAmount(stats?.revenue_year)} ₽</div>
          </div>
        </div>
      )}

      {/* Заказы требующие внимания */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[#0f172a]">Требуют внимания</h2>
          <Link to="/admin/orders" className="flex items-center gap-1 text-xs text-[#4A6FA5] font-medium">
            Все <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {attentionOrders.length === 0 ? (
          <p className="text-sm text-[#64748b] py-4 text-center">Все заказы обработаны</p>
        ) : (
          <div className="space-y-2">
            {attentionOrders.map(order => {
              const status = STATUS_LABELS[order.status] || { text: order.status, color: 'bg-[#64748b]/20 text-[#64748b]' };
              return (
                <Link
                  key={order.id}
                  to="/admin/orders"
                  className="flex items-center justify-between rounded-xl bg-[#f1f5f9] p-3 transition hover:bg-[#e2e8f0]"
                >
                  <div>
                    <div className="text-sm font-medium text-[#0f172a]">
                      #{String(order.id).slice(-5)}
                    </div>
                    <div className="text-xs text-[#64748b]">
                      {order.first_name || order.username || `ID: ${order.user_id}`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-[#0f172a]">{formatAmount(order.total_amount)} ₽</div>
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${status.color}`}>
                      {status.text}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
